/**
 * CR202: Header Validation Utilities
 * 
 * Validates header block configuration to prevent publishing empty headers.
 */

import type { HeaderBlockConfig, LayoutBlock } from "./types";
import { DEFAULT_HEADER_CONFIG } from "./types";
import type { AppAsset } from "@/lib/asset-studio/types";

export interface HeaderValidationResult {
  isValid: boolean;
  isEmpty: boolean;
  hasLogoWarning: boolean;
  warnings: HeaderValidationWarning[];
}

export interface HeaderValidationWarning {
  type: "empty_header" | "missing_logo_asset" | "logo_not_in_assets";
  message: string;
  severity: "error" | "warning";
}

/**
 * Validates header block configuration against available assets.
 */
export function validateHeaderConfig(
  headerBlock: LayoutBlock | undefined,
  headerConfig: HeaderBlockConfig,
  availableAssets: AppAsset[]
): HeaderValidationResult {
  const warnings: HeaderValidationWarning[] = [];
  
  // If header block is disabled, no validation needed
  if (!headerBlock?.enabled) {
    return { isValid: true, isEmpty: false, hasLogoWarning: false, warnings };
  }
  
  const { showLogo, showTitle, showSubtitle, logoAssetId } = headerConfig;
  
  // Check if header will render completely empty
  const willRenderEmpty = !showLogo && !showTitle && !showSubtitle;
  
  if (willRenderEmpty) {
    warnings.push({
      type: "empty_header",
      message: "Header will render empty. Enable logo, title, or subtitle to show content.",
      severity: "error",
    });
  }
  
  // Check if logo is enabled but asset is missing
  if (showLogo && logoAssetId) {
    const assetExists = availableAssets.some((a) => a.id === logoAssetId && a.status === "active");
    if (!assetExists) {
      warnings.push({
        type: "missing_logo_asset",
        message: "Selected logo asset not found or archived. Select a different logo or create one in Asset Studio.",
        severity: "error",
      });
    }
  }
  
  // Check if logo is enabled but no asset selected and no fallback
  if (showLogo && !logoAssetId && !headerConfig.logoUrl) {
    // This is actually OK - default logo behavior - but we could warn
    // For now, not a warning since the system has fallback behavior
  }
  
  const hasLogoWarning = warnings.some((w) => w.type === "missing_logo_asset");
  const isEmpty = willRenderEmpty;
  const isValid = warnings.filter((w) => w.severity === "error").length === 0;
  
  return { isValid, isEmpty, hasLogoWarning, warnings };
}

/**
 * Returns effective header display state for preview indicator.
 */
export function getHeaderPreviewState(
  headerBlock: LayoutBlock | undefined,
  headerConfig: HeaderBlockConfig,
  logoAsset: AppAsset | null | undefined
): {
  enabled: boolean;
  hasLogo: boolean;
  hasTitle: boolean;
  hasSubtitle: boolean;
  logoResolved: boolean;
  effectiveTitle: string;
  effectiveSubtitle: string;
} {
  const enabled = headerBlock?.enabled ?? true;
  const hasLogo = headerConfig.showLogo ?? DEFAULT_HEADER_CONFIG.showLogo;
  const hasTitle = headerConfig.showTitle ?? DEFAULT_HEADER_CONFIG.showTitle;
  const hasSubtitle = headerConfig.showSubtitle ?? DEFAULT_HEADER_CONFIG.showSubtitle;
  
  // Logo is considered resolved if:
  // - showLogo is false, OR
  // - No asset ID is set (uses default), OR
  // - Asset ID is set and asset exists
  const logoResolved = !hasLogo || !headerConfig.logoAssetId || !!logoAsset;
  
  return {
    enabled,
    hasLogo,
    hasTitle,
    hasSubtitle,
    logoResolved,
    effectiveTitle: headerConfig.titleText || DEFAULT_HEADER_CONFIG.titleText || "",
    effectiveSubtitle: headerConfig.subtitleText || DEFAULT_HEADER_CONFIG.subtitleText || "",
  };
}
